import type { CardFormat } from './types'
import type { TemplateDefinition } from './templateTypes'
import onlyjobs from './templates/onlyjobs'

/** Built-in templates shipped in code. Add new ones here. */
export const BUILT_IN_TEMPLATES: TemplateDefinition[] = [onlyjobs]

export const DEFAULT_TEMPLATE_ID = onlyjobs.id

// ── Custom templates (persisted per browser in localStorage) ──────────────────

const STORAGE_KEY = 'imageGenerator.customTemplates'

function readCustomTemplates(): TemplateDefinition[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed
      .filter(
        (t): t is TemplateDefinition =>
          t && typeof t.id === 'string' && typeof t.name === 'string' && t.templates
      )
      .map((t) => ({ ...t, isCustom: true }))
  } catch {
    return []
  }
}

function writeCustomTemplates(templates: TemplateDefinition[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(templates))
  } catch {
    /* quota / disabled storage — ignore, template stays for this session only */
  }
}

/** All templates available to the user: built-ins first, then custom. */
export function getAllTemplates(): TemplateDefinition[] {
  return [...BUILT_IN_TEMPLATES, ...readCustomTemplates()]
}

export function getTemplateById(id: string): TemplateDefinition | undefined {
  return getAllTemplates().find((t) => t.id === id)
}

/**
 * Persist a custom template. The HTML for the OTHER format defaults to the
 * built-in default so the unedited tab still renders; the user can upload that
 * format separately. Returns the saved definition.
 */
export function saveCustomTemplate(
  name: string,
  format: CardFormat,
  html: string
): TemplateDefinition {
  const id = `custom-${Date.now()}`
  const def: TemplateDefinition = {
    id,
    name,
    isCustom: true,
    templates: {
      feed: onlyjobs.templates.feed,
      story: onlyjobs.templates.story,
      [format]: html,
    },
  }
  const next = [...readCustomTemplates(), def]
  writeCustomTemplates(next)
  return def
}

/** Replace one format's HTML on an existing custom template. */
export function updateCustomTemplateFormat(
  id: string,
  format: CardFormat,
  html: string
): TemplateDefinition | undefined {
  const customs = readCustomTemplates()
  const idx = customs.findIndex((t) => t.id === id)
  if (idx === -1) return undefined
  const updated: TemplateDefinition = {
    ...customs[idx],
    templates: { ...customs[idx].templates, [format]: html },
  }
  customs[idx] = updated
  writeCustomTemplates(customs)
  return updated
}

export function deleteCustomTemplate(id: string): void {
  writeCustomTemplates(readCustomTemplates().filter((t) => t.id !== id))
}
